import { Component, Input, inject, signal, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { LocalCacheService } from '../../core/services/local-cache.service';
import { AuthService } from '../../auth/auth.service';
import { logout } from '../../store/auth/auth.actions';
import { selectUser, selectUserDisplayName, selectUserInitials, selectAuthLoading } from '../../store/auth/auth.selectors';
import { environment } from '../../../environments/environment';

type MenuItem = 'profile' | 'signout';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './header.html',
  styleUrls: ['./header.css']
})
export class Header implements OnInit, OnDestroy {
  @Input() username = '';

  private store = inject(Store);
  private router = inject(Router);
  private toastr = inject(ToastrService);
  private authService: AuthService = inject(AuthService);
  private cache = inject(LocalCacheService);
  private sub = new Subscription();

  // NgRx selectors
  user$ = this.store.select(selectUser);
  userDisplayName$ = this.store.select(selectUserDisplayName);
  userInitials$ = this.store.select(selectUserInitials);
  authLoading$ = this.store.select(selectAuthLoading);

  // Local state from NgRx
  private _userDisplayName = '';
  private _userInitials = '';
  private _userId = '';
  private _authLoading = false;

  /** True while SSO token validation is in progress — used to show skeleton. */
  get authLoading(): boolean {
    return this._authLoading;
  }

  // Always use LocalCacheService for userId (fallback for backward compat)
  get userId(): string {
    return this._userId || this.cache.userId || this.cache.userEmail || '';
  }

  // Use firstName from cache if available, fallback to displayName, then username Input
  get userDisplayName(): string {
    return this._userDisplayName || this.cache.firstName || this.cache.displayName || this.username || '';
  }

  // Toggle for menu visibility
  menuOpen = signal<boolean>(false);
  isSigningOut = signal<boolean>(false);

  ngOnInit(): void {
    this.sub.add(
      this.user$.subscribe(user => {
        this._userId = user?.id ?? '';
        this._userDisplayName = user ? `${user.firstName} ${user.lastName}`.trim() : '';
        this._userInitials = user ? `${user.firstName?.charAt(0) ?? ''}${user.lastName?.charAt(0) ?? ''}`.toUpperCase() : '';
      })
    );
    this.sub.add(
      this.authLoading$.subscribe(loading => {
        this._authLoading = loading;
      })
    );
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }

  toggleMenu(): void {
    this.menuOpen.set(!this.menuOpen());
  }

  closeMenu(): void {
    this.menuOpen.set(false);
  }

  onMenu(item: MenuItem): void {
    switch (item) {
      case 'profile':
        this.router.navigate(['/profile']);
        break;
      case 'signout':
        this.handleSignOut();
        break;
    }
    this.closeMenu();
  }


  private handleSignOut(): void {
    if (this.isSigningOut()) return;
    this.isSigningOut.set(true);

    // Dispatch NgRx logout action (effects will handle the rest)
    this.store.dispatch(logout());
    this.toastr.success('You have been signed out.', 'Sign Out');
    this.isSigningOut.set(false);
  }

  displayName(): string {
    const safe = (this.userDisplayName || '').trim();
    return safe || 'Guest';
  }

  /** Extract initials from name (max 2 letters), fallback to "G" */
  computeInitials(): string {
    if (this._userInitials) return this._userInitials;
    const safe = (this.userDisplayName || 'G').trim();
    if (!safe) return 'G';
    const parts = safe.split(/\s+/).filter(Boolean);
    const initials = parts.map(p => p[0]).join('').slice(0, 2).toUpperCase();
    return initials || 'G';
  }
}
